import React, { useState } from "react";
import { useAppSelector } from "utils/redux/hooks";
import { Button } from "app/shadcnComponents/ui/button";
import { Card } from "app/shadcnComponents/ui/card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "app/lib/utils";
import { selectFileSyncLogs, selectGetLogsStatus } from "./slice";
import { FileSyncLog, SyncLogStatus } from "./types";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { getS } from "utils/helperFunctions";

type FilterType = "all" | "errors";

const STATUS_CONFIG: Record<
  SyncLogStatus,
  { label: string; className: string; icon: IconName }
> = {
  pending: {
    label: "Pending",
    className: "bg-[var(--charcoal-10)] text-[var(--charcoal-80)]",
    icon: "clock",
  },
  processing: {
    label: "Processing",
    className: "bg-[var(--sapphire-10)] text-[var(--sapphire-100)]",
    icon: "arrow-rotate-right",
  },
  success: {
    label: "Completed",
    className: "bg-[var(--success-green-20)] text-[var(--success-green-100)]",
    icon: "check",
  },
  partial: {
    label: "Partial",
    className: "bg-amber-100 text-amber-700",
    icon: "exclamation",
  },
  failed: {
    label: "Failed",
    className: "bg-[var(--error-red-10)] text-[var(--error-red-100)]",
    icon: "xmark",
  },
  quarantined: {
    label: "Quarantined",
    className: "bg-amber-100 text-amber-700",
    icon: "circle-xmark",
  },
  approved: {
    label: "Approved",
    className: "bg-[var(--success-green-20)] text-[var(--success-green-100)]",
    icon: "check",
  },
  rejected: {
    label: "Rejected",
    className: "bg-[var(--error-red-10)] text-[var(--error-red-100)]",
    icon: "xmark",
  },
  expired: {
    label: "Expired",
    className: "bg-[var(--charcoal-10)] text-[var(--charcoal-60)]",
    icon: "clock",
  },
};

const FileSyncHistoryTab = () => {
  const logs = useAppSelector(selectFileSyncLogs);
  const getLogsStatus = useAppSelector(selectGetLogsStatus);
  const [filter, setFilter] = useState<FilterType>("all");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const filteredLogs =
    filter === "all"
      ? logs
      : logs.filter(
          (log) =>
            log.status === "failed" ||
            log.status === "partial" ||
            log.rows_with_errors > 0
        );

  const toggleRow = (logId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const formatDuration = (startedAt: string, completedAt: string | null) => {
    if (!completedAt) return "-";
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const seconds = (end - start) / 1000;
    return seconds < 60
      ? `${seconds.toFixed(1)}s`
      : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  const renderChanges = (log: FileSyncLog) => {
    const changes = [];
    if (log.users_added > 0 || log.users_to_add > 0) {
      changes.push({
        icon: "plus",
        label: `${log.users_added || log.users_to_add} added`,
        className: "text-[var(--success-green-100)]",
        bgClass: "bg-[var(--success-green-20)]",
      });
    }
    if (log.users_updated > 0 || log.users_to_update > 0) {
      changes.push({
        icon: "arrow-rotate-right",
        label: `${log.users_updated || log.users_to_update} updated`,
        className: "text-[var(--sapphire-100)]",
        bgClass: "bg-[var(--sapphire-10)]",
      });
    }
    if (log.users_deactivated > 0 || log.users_to_deactivate > 0) {
      changes.push({
        icon: "xmark",
        label: `${
          log.users_deactivated || log.users_to_deactivate
        } deactivated`,
        className: "text-[var(--error-red-100)]",
        bgClass: "bg-[var(--error-red-10)]",
      });
    }
    if (log.users_reactivated || log.users_to_reactivate) {
      changes.push({
        icon: "rotate-right",
        label: `${
          log.users_reactivated || log.users_to_reactivate
        } reactivated`,
        className: "text-teal-700",
        bgClass: "bg-teal-100",
      });
    }
    if (log.rows_with_errors > 0) {
      changes.push({
        icon: "exclamation",
        label: `${log.rows_with_errors} errors`,
        className: "text-amber-700",
        bgClass: "bg-amber-100",
      });
    }

    if (changes.length === 0 && log.error_message) {
      return (
        <span className="text-sm text-[var(--error-red-100)]">
          {log.error_message.substring(0, 50)}
          {log.error_message.length > 50 ? "..." : ""}
        </span>
      );
    }

    return (
      <div className="flex gap-3 flex-wrap">
        {changes.map((change, idx) => (
          <div key={idx} className="flex items-center gap-1.5 text-sm">
            <span
              className={cn(
                "w-5 h-5 rounded flex items-center justify-center text-xs",
                change.bgClass,
                change.className
              )}
            >
              <FontAwesomeIcon icon={change.icon as IconName} />
            </span>
            <span>{change.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const getTableContent = () => {
    if (filteredLogs.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-4 py-12 text-center">
            <div className="text-[var(--charcoal-60)]">
              <FontAwesomeIcon
                icon={["far", "clock"]}
                className="text-4xl mb-4"
              />
              <p className="text-lg font-medium mb-1">No sync history yet</p>
              <p className="text-sm">
                Syncs will appear here once they start running
              </p>
            </div>
          </td>
        </tr>
      );
    }

    return filteredLogs.map((log) => {
      const isExpanded = expandedRows.has(log.id);
      const { date, time } = formatDate(log.started_at);
      const statusConfig = STATUS_CONFIG[log.status];

      return (
        <React.Fragment key={log.id}>
          <tr
            className="border-b border-[var(--charcoal-10)] hover:bg-[var(--sapphire-05)] cursor-pointer"
            onClick={() => toggleRow(log.id)}
          >
            <td className="px-4 py-4">
              <span
                className={cn(
                  "inline-block transition-transform",
                  isExpanded && "rotate-90"
                )}
              >
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="text-[var(--charcoal-40)]"
                />
              </span>
            </td>
            <td className="px-4 py-4">
              <div className="font-medium">{date}</div>
              <div className="text-sm text-[var(--charcoal-60)]">{time}</div>
            </td>
            <td className="px-4 py-4">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium",
                  statusConfig.className
                )}
              >
                <FontAwesomeIcon icon={statusConfig.icon as any} />
                {statusConfig.label}
              </span>
            </td>
            <td className="px-4 py-4">{renderChanges(log)}</td>
            <td className="px-4 py-4 text-sm">
              {formatDuration(log.started_at, log.completed_at)}
            </td>
            <td className="px-4 py-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary-gray"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(log.id);
                  }}
                >
                  Expand
                </Button>
              </div>
            </td>
          </tr>
          {isExpanded && (
            <tr className="bg-[var(--charcoal-05)]">
              <td colSpan={6} className="px-4 py-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {getUsersAdded(log)}
                  {getFieldUpdates(log)}
                  {getDeactivatedUsers(log)}
                  {getReactivatedUsers(log)}
                  {getFileInfo(log)}
                  {getErrorDetails(log)}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };
  const getFileInfo = (log: FileSyncLog) => (
    <div className="bg-white p-4 rounded-lg">
      <div className="text-xs uppercase tracking-wide text-[var(--charcoal-60)] mb-3">
        File Info
      </div>
      <div className="space-y-1 text-sm">
        <div className="break-words">{log.file_name}</div>
        <div>{log.total_rows_in_file} rows processed</div>
        {log.file_size_bytes && (
          <div>{(log.file_size_bytes / 1024).toFixed(1)} KB</div>
        )}
      </div>
    </div>
  );

  const getChangeListItem = (label: string, count?: number) => {
    if (!count) {
      return null;
    }
    return (
      <li className="marker:mr-0">
        {count} {label}
        {getS(count)}
      </li>
    );
  };

  const getFieldUpdates = (log: FileSyncLog) => {
    if (log.users_updated === 0 && !log.details?.usersToUpdate?.length) {
      return null;
    }

    return (
      <div className="bg-white p-4 rounded-lg">
        <div className="text-xs uppercase tracking-wide text-[var(--charcoal-60)] mb-3">
          Field Updates (
          {log.users_updated || log.details?.usersToUpdate?.length || 0})
        </div>
        <div className="space-y-1 text-sm">
          <ul className="list-disc list-inside ps-1 mb-0">
            {getChangeListItem(
              "name change",
              log.details?.usersWithNameChanges?.length
            )}
            {getChangeListItem(
              "job title change",
              log.details?.usersWithJobTitleChanges?.length
            )}
            {getChangeListItem(
              "department change",
              log.details?.usersWithDepartmentChanges?.length
            )}
            {getChangeListItem(
              "manager change",
              log.details?.usersWithManagerChanges?.length
            )}
          </ul>
        </div>
      </div>
    );
  };

  const getUsersAdded = (log: FileSyncLog) => {
    if (log.users_added === 0 && !log.details?.usersToAdd?.length) return null;
    return (
      <div className="bg-white p-4 rounded-lg min-w-0">
        <div className="text-xs uppercase tracking-wide text-[var(--charcoal-60)] mb-3">
          Users Added ({log.users_added || log.details?.usersToAdd?.length || 0}
          )
        </div>
        <div className="space-y-1 text-sm">
          {log.details?.usersToAdd?.slice(0, 5).map((user) => (
            <div
              key={user.emailAddress}
              className="flex items-start gap-2 min-w-0"
            >
              <span className="text-[var(--success-green-100)] shrink-0 mt-0.5">
                •
              </span>
              <span className="truncate hover:whitespace-normal hover:break-all hover:text-clip">
                {user.firstName} {user.lastName} ({user.emailAddress})
              </span>
            </div>
          ))}
          {(log.details?.usersToAdd?.length ?? 0) > 5 && (
            <div className="text-[var(--charcoal-60)]">
              +{(log.details?.usersToAdd?.length ?? 0) - 5} more
            </div>
          )}
        </div>
      </div>
    );
  };

  const getErrorDetails = (log: FileSyncLog) => {
    const hasRowErrors =
      log.details?.errors && Object.keys(log.details.errors).length > 0;
    if (!log.error_message && !hasRowErrors) return null;
    return (
      <div className="bg-white p-4 rounded-lg md:col-span-3">
        <div className="text-xs uppercase tracking-wide text-[var(--error-red-100)] mb-3">
          Error Details
        </div>
        {log.error_message && (
          <div className="text-sm text-[var(--error-red-100)] break-words">
            {log.error_message}
          </div>
        )}
        {hasRowErrors && (
          <div className={cn("space-y-2", log.error_message && "mt-3")}>
            {Object.entries(log.details?.errors ?? {}).map(
              ([rowKey, fields]) => (
                <div key={rowKey} className="text-sm">
                  <div className="font-medium text-[var(--charcoal-80)]">
                    Row {rowKey}
                  </div>
                  <ul className="list-disc list-inside ps-1 mb-0">
                    {Object.values(fields)
                      .flat()
                      .map((err, idx) => (
                        <li
                          key={idx}
                          className="text-[var(--error-red-100)] break-words"
                        >
                          {err.message}
                        </li>
                      ))}
                  </ul>
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const getReactivatedUsers = (log: FileSyncLog) => {
    if (log.users_reactivated === 0 && !log.details?.usersToReactivate?.length)
      return null;
    return (
      <div className="bg-white p-4 rounded-lg min-w-0">
        <div className="text-xs uppercase tracking-wide text-[var(--charcoal-60)] mb-3">
          Users Reactivated (
          {log.users_reactivated || log.details?.usersToReactivate?.length || 0}
          )
        </div>
        <div className="space-y-1 text-sm">
          {log.details?.usersToReactivate?.slice(0, 5).map((user) => (
            <div
              key={user.emailAddress}
              className="flex items-start gap-2 min-w-0"
            >
              <span className="text-teal-600 shrink-0 mt-0.5">•</span>
              <span className="truncate hover:whitespace-normal hover:break-all hover:text-clip">
                {user.firstName} {user.lastName} ({user.emailAddress})
              </span>
            </div>
          ))}
          {(log.details?.usersToReactivate?.length ?? 0) > 5 && (
            <div className="text-[var(--charcoal-60)]">
              +{(log.details?.usersToReactivate?.length ?? 0) - 5} more
            </div>
          )}
        </div>
      </div>
    );
  };

  const getDeactivatedUsers = (log: FileSyncLog) => {
    if (log.users_deactivated === 0 && !log.details?.usersToDeactivate?.length)
      return null;
    return (
      <div className="bg-white p-4 rounded-lg min-w-0">
        <div className="text-xs uppercase tracking-wide text-[var(--charcoal-60)] mb-3">
          Users Deactivated (
          {log.users_deactivated || log.details?.usersToDeactivate?.length || 0}
          )
        </div>
        <div className="space-y-1 text-sm">
          {log.details?.usersToDeactivate?.slice(0, 5).map((user) => (
            <div
              key={user.emailAddress}
              className="flex items-start gap-2 min-w-0"
            >
              <span className="text-[var(--error-red-100)] shrink-0 mt-0.5">
                •
              </span>
              <span className="truncate hover:whitespace-normal hover:break-all hover:text-clip">
                {user.emailAddress}
              </span>
            </div>
          ))}
          {(log.details?.usersToDeactivate?.length ?? 0) > 5 && (
            <div className="text-[var(--charcoal-60)]">
              +{(log.details?.usersToDeactivate?.length ?? 0) - 5} more
            </div>
          )}
        </div>
      </div>
    );
  };

  if (getLogsStatus === "loading") {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--sapphire-100)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter and Export */}
      <div className="flex justify-between items-center">
        <div className="flex bg-[var(--charcoal-05)] p-1 rounded-lg">
          {(["all", "errors"] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
                filter === f
                  ? "bg-white text-[var(--charcoal-100)] shadow-sm"
                  : "text-[var(--charcoal-60)] hover:text-[var(--charcoal-80)]"
              )}
            >
              {f === "all" ? "All" : "Errors Only"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--charcoal-05)] border-b border-[var(--charcoal-10)]">
                <th className="w-10 px-4 py-3" />
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Date & Time
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Changes
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Duration
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>{getTableContent()}</tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {filteredLogs.length > 0 && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-[var(--charcoal-60)]">
            Showing 1-{filteredLogs.length} of {filteredLogs.length} syncs
          </span>
          <div className="flex gap-2">
            <Button variant="secondary-gray" size="sm" disabled>
              <FontAwesomeIcon icon="chevron-left" className="mr-2" />
              Previous
            </Button>
            <Button variant="secondary-gray" size="sm" disabled>
              Next
              <FontAwesomeIcon icon="chevron-right" className="ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileSyncHistoryTab;
